import type { ComponentPropsWithRef, ReactNode } from 'react'

import { Icon, type IconName } from './Icon'

/**
 * A control, whichever element it happens to be.
 *
 * Everything MyLib can be told to do goes through one of these, and they all keep the same target
 * size — `--control-min`, 44px at the base font size — whether they shout or whisper. A quiet
 * control is quieter to look at, never harder to hit. This is also the reason nothing in MyLib is
 * reachable by gesture alone: a gesture is a shortcut for a control that is already on the screen.
 */
export type ControlTone =
  /** The one thing this screen is for. At most one per view. */
  | 'primary'
  /** An ordinary alternative — bordered, so it is visibly a control. */
  | 'quiet'
  /** Sits inside something that already has an edge, and adds none of its own. */
  | 'bare'
  /**
   * Floats over a surface that is not a page — a canvas above all. It brings its own background
   * and edge, because there is nothing behind it that can be relied on to read against.
   */
  | 'floating'

function classesFor(tone: ControlTone, iconOnly: boolean, extra?: string): string {
  return ['control', `control--${tone}`, iconOnly ? 'control--icon' : null, extra]
    .filter((part) => part !== null && part !== undefined && part !== '')
    .join(' ')
}

export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'className'> {
  readonly tone?: ControlTone
  readonly icon?: IconName
  /**
   * When set, only the picture is shown and this becomes the control's name. A control with a
   * picture and no name is a control nobody can read out loud.
   */
  readonly iconOnlyLabel?: string
  readonly className?: string
  readonly children?: ReactNode
}

export function Button({
  tone = 'quiet',
  icon,
  iconOnlyLabel,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconOnly = iconOnlyLabel !== undefined
  return (
    <button
      {...rest}
      type={type}
      className={classesFor(tone, iconOnly, className)}
      aria-label={iconOnly ? iconOnlyLabel : rest['aria-label']}
      /*
        A control that is only a picture says what it is on hovering, as well as to a screen reader.
        The name is already written once, for accessibility; not showing it was the difference
        between a row of icons somebody can read and a row they have to try out. It is a hint and
        never the name — the name is the `aria-label`, and it is still there when nothing hovers.
      */
      title={iconOnly ? iconOnlyLabel : rest.title}
    >
      {icon !== undefined && <Icon name={icon} />}
      {!iconOnly && children}
    </button>
  )
}

export interface LinkProps extends Omit<ComponentPropsWithRef<'a'>, 'className'> {
  readonly tone?: ControlTone
  readonly icon?: IconName
  readonly className?: string
  readonly children?: ReactNode
}

/** A link that acts as a control. Still a link, so it can be opened in a tab of its own. */
export function LinkButton({ tone = 'quiet', icon, className, children, ...rest }: LinkProps) {
  return (
    <a {...rest} className={classesFor(tone, false, className)}>
      {icon !== undefined && <Icon name={icon} />}
      {children}
    </a>
  )
}
